import React from "react";
import { X } from "feather-icons-react/build/IconComponents";
import FeatherIcon from "feather-icons-react";
import { Form, Input, Button, Select, Card, Tooltip } from "antd";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import UserGroupManager from "./userGroupManager";

const PERMISSION_TYPES = ["create", "edit", "delete", "download", "view"];
const permissionLabels = {
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  download: "Download",
  view: "View",
};

const EditPermissionLayout = ({
  ref,
  isVisible,
  isFullScreen,
  setIsFullScreen,
  onClose,
  isNewUser,
  isViewMode,
  userData,
  setUserData,
  form,
  loading,
  data,
  confirmPassword,
  setConfirmPassword,
  adminPassword,
  setAdminPassword,
  selectedPage,
  handleSelectPage,
  pagePermissionOptions,
  selectedGroupId,
  pagePermissions,
  handleTogglePermission,
  tableData,
  handleTogglePermissionInTable,
  handleRemovePage,
  handleSelectAllForPage,
  handleSelectAllPermissions,
  handleAddAllPagesWithAllPermissions,
  CLIENT_OPTIONS,
  clientPermissions,
  isEditingSelf = false,
  handleClientChange,
  availableGroups,
  setSelectedGroupId,
  setShowGroupManager,
  showGroupManager,
  generateStrongPassword,
  handleSavePermission,
  handleCancel,
  otpVerified,
  otpSent,
  sendingOtp,
  handleSendOtpClick,
  enteredOtp,
  setEnteredOtp,
  generatedOtp,
  remainingTime,
  handleRoleChange,
  setOtpVerified,
}) => {
  return (
    <div
      ref={ref}
      className={`right-sidebar ${isVisible ? "visible" : ""}`}
      style={{
        position: "fixed",
        top: data ? 0 : 66,
        right: 0,
        height: "100%",
        zIndex: 1050,
        width: isFullScreen ? "60%" : "555px",
        backgroundColor: "#fff",
        boxShadow: "0 0 3px rgba(0, 0, 0, 0.6)",
        overflowY: "auto",
        transition: "transform 0.3s ease-in-out",
        transform: isVisible ? "translateX(0)" : "translateX(100%)",
      }}
    >
      {/* Sidebar Header */}
      <div
        className="sidebar-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "15px",
          borderBottom: "1px solid #ddd",
        }}
      >
        <h5>
          {isNewUser
            ? "New User"
            : isViewMode
            ? "View User"
            : `Edit User - ${userData?.email || ""}`}
        </h5>
        <div>
          <button
            onClick={() => setIsFullScreen(!isFullScreen)}
            className="btn"
            title={isFullScreen ? "Minimize" : "Maximize"}
          >
            <FeatherIcon icon={isFullScreen ? "minimize" : "maximize"} />
          </button>
          <button
            className="btn"
            onClick={() => {
              onClose();
            }}
          >
            <X />
          </button>
        </div>
      </div>
      {/* Sidebar Body */}
      <div style={{ padding: 24, paddingBottom: 80 }}>
        <Form form={form} layout="vertical" onFinish={handleSavePermission}>
          {/* --- User Fields --- */}
          <Card title="User Details">
            <div className="row">
              <div className="col-12 col-sm-6 mb-3">
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[{ required: true, type: "email" }]}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Input
                      size="medium"
                      style={{ width: 800 }}
                      value={userData?.email || ""}
                      onChange={(e) =>
                        setUserData({ ...userData, email: e.target.value })
                      }
                      disabled={
                        isViewMode ||
                        (!isNewUser &&
                          userData?.role !== "senior_accountant" &&
                          userData?.role !== "management")
                      }
                    />
                    {otpVerified && (
                      <CheckCircleOutlined
                        style={{ color: "#52c41a", fontSize: 18 }}
                      />
                    )}
                  </div>
                </Form.Item>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-sm-6 mb-3">
                <Form.Item label="First Name" name="first_name">
                  <Input
                    value={userData?.first_name || ""}
                    onChange={(e) =>
                      setUserData({ ...userData, first_name: e.target.value })
                    }
                    disabled={isViewMode}
                  />
                </Form.Item>
              </div>
              <div className="col-12 col-sm-6 mb-3">
                <Form.Item label="Last Name" name="last_name">
                  <Input
                    value={userData?.last_name || ""}
                    onChange={(e) =>
                      setUserData({ ...userData, last_name: e.target.value })
                    }
                    disabled={isViewMode}
                  />
                </Form.Item>
              </div>
            </div>
          </Card>
          {/* --- Security --- */}
          <Card title="Security Details" className="mt-3">
            <div className="row">
              <div className="col-12 col-sm-6">
                <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                    {
                      required: isNewUser,
                      message: "Password is required",
                    },
                    {
                      validator: (_, value) => {
                        if (!value && !isNewUser) return Promise.resolve();
                        const weakPasswords = [
                          "12345678",
                          "password",
                          "admin123",
                          "qwerty123",
                          "letmein",
                        ];

                        if (value && value.length < 8) {
                          return Promise.reject(
                            "Password must be at least 8 characters long"
                          );
                        }

                        if (
                          value &&
                          weakPasswords.includes(value.toLowerCase())
                        ) {
                          return Promise.reject(
                            "Password is too common or insecure"
                          );
                        }

                        const strongRegex =
                          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

                        if (value && !strongRegex.test(value)) {
                          return Promise.reject(
                            "Password must include uppercase, lowercase, number, and special character"
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input.Password
                    value={userData?.password || ""}
                    onChange={(e) =>
                      setUserData({ ...userData, password: e.target.value })
                    }
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    disabled={isViewMode}
                  />
                </Form.Item>
              </div>
              <div className="col-12 col-sm-6">
                <Form.Item
                  label="Confirm Password"
                  name="confirm_password"
                  dependencies={["password"]}
                  rules={[
                    {
                      required: isNewUser,
                      message: "Please confirm your password",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value && !isNewUser) return Promise.resolve();
                        if (value !== getFieldValue("password")) {
                          return Promise.reject("Passwords do not match");
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    disabled={isViewMode}
                  />
                </Form.Item>
              </div>
              {/* Generate Password Button below both inputs */}
              <div
                className="col-12 col-sm-6 d-flex justify-content-start align-items-start"
                style={{ marginTop: "4px" }}
              >
                <Button
                  type="default"
                  size="small"
                  style={{ padding: "2px 8px", fontSize: "13px" }}
                  onClick={() => {
                    const newPass = generateStrongPassword();
                    form.setFieldsValue({
                      password: newPass,
                      confirm_password: newPass,
                    });
                    setUserData({ ...userData, password: newPass });
                    setConfirmPassword(newPass);
                  }}
                  disabled={isViewMode}
                >
                  Generate Strong Password
                </Button>
              </div>
            </div>
            {!isNewUser && userData?.password && (
              <div className="row">
                <div className="col-12 col-sm-6 mb-3">
                  <Form.Item
                    label="Admin Password"
                    name="admin_password"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter your password to confirm changes",
                      },
                    ]}
                  >
                    <Input.Password
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      iconRender={(visible) =>
                        visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                      }
                      disabled={isViewMode}
                    />
                  </Form.Item>
                </div>
              </div>
            )}
          </Card>
          {/* --- Access Control --- */}
          <Card title="Access Control" className="mt-3">
            <div className="row">
              <div className="col-12 col-sm-6 mb-3">
                <Form.Item
                  label="Role"
                  name="role"
                  rules={[{ required: true }]}
                  help={
                    isEditingSelf
                      ? "You cannot change your own role. Ask another administrator to change it."
                      : undefined
                  }
                >
                  <Select
                    onChange={handleRoleChange}
                    disabled={isViewMode || isEditingSelf}
                    title={
                      isEditingSelf
                        ? "You cannot change your own role. Ask another administrator to change it."
                        : undefined
                    }
                  >
                    <Select.Option value="user">User</Select.Option>
                    <Select.Option value="admin">Admin</Select.Option>
                    <Select.Option value="normal_user">
                      Normal User
                    </Select.Option>
                    <Select.Option value="senior_accountant">
                      Senior Accountant
                    </Select.Option>
                    <Select.Option value="management">Management</Select.Option>
                    <Select.Option value="super_admin">
                      Super Admin
                    </Select.Option>
                  </Select>
                </Form.Item>
              </div>
              {userData?.role !== "admin" &&
                userData?.role !== "super_admin" && (
                  <div className="col-12 col-sm-6 mb-3">
                    <Form.Item label="User Group">
                      <div className="d-flex align-items-center gap-2">
                        <Select
                          value={selectedGroupId}
                          onChange={(v) => setSelectedGroupId(v)}
                          disabled={isViewMode}
                          placeholder="Select a group"
                          className="flex-grow-1"
                        >
                          {availableGroups.map((group) => (
                            <Select.Option key={group.id} value={group.id}>
                              {group.name}
                            </Select.Option>
                          ))}
                        </Select>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setShowGroupManager(true)}
                        >
                          Manage
                        </button>
                      </div>
                    </Form.Item>
                  </div>
                )}
            </div>
          </Card>
          {/* --- Page Permissions --- */}
          <Card title="Page Permissions" className="mt-3">
            <div className="row">
              <div className="col-6">
                <label className="form-label">Page</label>

                <Select
                  style={{ width: "100%" }}
                  placeholder="Select a page"
                  value={selectedPage || ""}
                  onChange={handleSelectPage}
                  options={[
                    { label: "Select All Pages", value: "__all__" },
                    ...pagePermissionOptions
                      .filter((option) => {
                        return option && option.value && option.label;
                      })
                      .sort((a, b) => a.label.localeCompare(b.label)),
                  ]}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>

              {!selectedGroupId && (
                <div className="col-12 mt-2">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Select a User Group first to see available page permissions
                  </small>
                </div>
              )}
              <div className="col-6 d-flex flex-column justify-content-center">
                {selectedPage ? (
                  <div className="d-flex flex-column align-items-start">
                    {/* Labels Row */}
                    <div className="d-flex mb-1" style={{ gap: 12 }}>
                      {PERMISSION_TYPES.map((type) => (
                        <div
                          key={`label-${type}`}
                          style={{
                            width: 40,
                            textAlign: "center",
                            fontSize: 11,
                            color: "#888",
                          }}
                        >
                          {permissionLabels[type]}
                        </div>
                      ))}
                    </div>

                    {/* Icons Row */}
                    <div className="d-flex" style={{ gap: 12 }}>
                      {PERMISSION_TYPES.map((action) => {
                        // Check if the selected page has this permission
                        const isActive =
                          selectedPage &&
                          pagePermissions[selectedPage] &&
                          pagePermissions[selectedPage].includes(action);

                        return (
                          <div
                            key={`icon-${action}`}
                            style={{ width: 40, textAlign: "center" }}
                          >
                            <Tooltip title={permissionLabels[action]}>
                              {isActive ? (
                                <MinusCircleOutlined
                                  style={{
                                    fontSize: 24,
                                    color: "#ff4d4f",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleTogglePermission(action)}
                                />
                              ) : (
                                <PlusCircleOutlined
                                  style={{
                                    fontSize: 24,
                                    color: "#52c41a",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleTogglePermission(action)}
                                />
                              )}
                            </Tooltip>
                            <div
                              style={{
                                fontSize: 11,
                                color: "#888",
                              }}
                            >
                              {permissionLabels[action]}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <span className="text-muted mt-2">
                    Select a page to add permissions
                  </span>
                )}
              </div>
            </div>
            <div className="mt-3">
              <div className="d-flex gap-2 mb-2">
                <Button
                  type="primary"
                  size="small"
                  onClick={handleAddAllPagesWithAllPermissions}
                >
                  Add All Pages (Full Access)
                </Button>
                {tableData.length > 0 && (
                  <Button
                    type="default"
                    size="small"
                    onClick={handleSelectAllPermissions}
                  >
                    Select All Permissions
                  </Button>
                )}
              </div>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Page View</th>
                    <th>Authorizations</th>
                    <th style={{ width: 50 }} className="text-center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row) => (
                    <tr key={row.key}>
                      <td>{row.page}</td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            alignItems: "center",
                          }}
                        >
                          {PERMISSION_TYPES.map((type) => {
                            const isGranted = row.actions.includes(type);
                            const label = permissionLabels[type];

                            return (
                              <div
                                key={`${row.pageRaw}-${type}`}
                                style={{ textAlign: "center" }}
                              >
                                <div style={{ fontSize: 9, marginBottom: 4 }}>
                                  {label}
                                </div>
                                {isGranted ? (
                                  <CheckCircleOutlined
                                    style={{
                                      fontSize: 16,
                                      color: "#52c41a",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      handleTogglePermissionInTable(
                                        row.pageRaw,
                                        type
                                      )
                                    }
                                  />
                                ) : (
                                  <MinusCircleOutlined
                                    style={{
                                      fontSize: 16,
                                      color: "#d9d9d9",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      handleTogglePermissionInTable(
                                        row.pageRaw,
                                        type
                                      )
                                    }
                                  />
                                )}
                              </div>
                            );
                          })}
                          <Tooltip title="Select All Permissions">
                            <Button
                              type="link"
                              size="small"
                              style={{
                                marginLeft: 8,
                                padding: "0 4px",
                                fontSize: 11,
                              }}
                              onClick={() =>
                                handleSelectAllForPage(row.pageRaw)
                              }
                            >
                              All
                            </Button>
                          </Tooltip>
                        </div>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemovePage(row.pageRaw)}
                          type="button"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          {/* --- Client Permissions --- */}
          <Card title="Client Permissions" className="mt-3">
            <Form.Item label="Clients" name="client_permission">
              <Select
                mode="multiple"
                allowClear
                style={{ width: "50%" }}
                placeholder="Select clients"
                value={clientPermissions.length > 0 ? clientPermissions : []}
                onChange={handleClientChange}
                disabled={isViewMode}
                maxTagRender={(props) => {
                  if (clientPermissions.length === 0) {
                    return (
                      <span style={{ padding: "0 8px" }}>Select Clients</span>
                    );
                  }
                  return props.label;
                }}
              >
                {CLIENT_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Card>
          <Card title="User Verification" className="mt-3">
            {isNewUser && (
              <div className="d-flex flex-wrap align-items-center gap-3">
                <Button
                  type="primary"
                  loading={sendingOtp}
                  onClick={handleSendOtpClick}
                  disabled={!userData?.email || isViewMode || otpSent}
                  style={{
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    transition: "all 0.3s ease-in-out",
                  }}
                  className="send-otp-btn"
                >
                  {otpSent ? "OTP Sent" : "Send OTP"}
                </Button>
                {otpSent && (
                  <div
                    className="d-flex flex-column"
                    style={{ marginTop: "22px" }}
                  >
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <Input
                        placeholder="Enter OTP"
                        value={enteredOtp}
                        onChange={(e) => setEnteredOtp(e.target.value)}
                        style={{ width: 150 }}
                        disabled={remainingTime <= 0 || otpVerified}
                      />

                      <Button
                        type="dashed"
                        onClick={() => {
                          if (enteredOtp === generatedOtp) {
                            setOtpVerified(true);
                          } else {
                            // Error handling is in the parent component
                          }
                        }}
                        disabled={
                          remainingTime <= 0 || !enteredOtp || otpVerified
                        }
                      >
                        Verify User OTP
                      </Button>
                    </div>
                    <span className="text-muted mt-2" style={{ fontSize: 13 }}>
                      Expires in: {Math.floor(remainingTime / 60)}:
                      {(remainingTime % 60).toString().padStart(2, "0")}
                    </span>
                  </div>
                )}
              </div>
            )}
          </Card>
          {/* --- Save/Cancel Buttons --- */}
          <div className="row mt-4">
            <div className="col-12 d-flex justify-content-end">
              <Button onClick={handleCancel} className="me-2">
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={isNewUser && !otpVerified}
              >
                {isNewUser ? "Create User" : "Save Changes"}
              </Button>
            </div>
          </div>
          <UserGroupManager
            visible={showGroupManager}
            onClose={() => {
              setShowGroupManager(false);
            }}
          />
        </Form>
      </div>
    </div>
  );
};

export default EditPermissionLayout;
